import { cn, GlassAvatar } from '@merritrac/shared/components';
import { ArrowLeft, LogOut, Moon, Search, Sun } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { useAuth } from '../../lib/auth.js';
import { useHeaderConfig } from '../../lib/headerConfig.js';
import { useTheme } from '../../lib/theme.js';

import type { ReactElement } from 'react';

interface HeaderProps {
  title: string;
}

function getUserName(user: { email: string } | null): string {
  if (!user) {
    return 'User';
  }
  const name = user.email.split('@')[0] ?? 'User';
  return name.replace(/[._-]/g, ' ');
}

export function Header({ title }: HeaderProps): ReactElement {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const { title: overrideTitle, backPath } = useHeaderConfig();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleLogout = useCallback((): void => {
    logout();
    void navigate('/login');
  }, [logout, navigate]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) {
      document.addEventListener('mousedown', onClickOutside);
    }
    return (): void => {
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, [menuOpen]);

  const ThemeIcon = theme === 'light' ? Moon : Sun;

  return (
    <header
      className={cn(
        'sticky top-0 z-20 flex items-center justify-between',
        'h-[var(--header-h)] px-6',
        'bg-[var(--glass-bg)] backdrop-blur-[var(--glass-blur-medium)]',
        'border-b border-[var(--glass-border-subtle)]',
      )}
    >
      <div className="flex items-center gap-2">
        {backPath !== null && (
          <button
            type="button"
            onClick={() => {
              void navigate(backPath);
            }}
            className={cn(
              'flex items-center justify-center w-8 h-8 rounded-lg',
              'text-text-secondary hover:text-text',
              'hover:bg-[var(--glass-bg)] transition-all duration-[var(--dur)] ease-[var(--ease)]',
              'cursor-pointer',
            )}
            aria-label="Go back"
          >
            <ArrowLeft size={16} />
          </button>
        )}
        <h1 className="text-lg font-semibold text-text">{overrideTitle ?? title}</h1>
      </div>

      <div className="flex items-center gap-3">
        {/* Search */}
        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary"
          />
          <input
            type="search"
            placeholder="Search…"
            className={cn(
              'h-8 w-56 pl-9 pr-3 rounded-lg text-sm text-text',
              'bg-[var(--glass-bg-subtle)] border border-[var(--glass-border-subtle)]',
              'backdrop-blur-sm',
              'placeholder:text-text-tertiary',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary',
              'focus:shadow-[var(--glass-glow)]',
              'transition-all duration-[var(--dur)] ease-[var(--ease)]',
            )}
          />
        </div>

        {/* Theme toggle */}
        <button
          type="button"
          onClick={toggle}
          className={cn(
            'flex items-center justify-center w-8 h-8 rounded-lg',
            'text-text-secondary hover:text-text',
            'hover:bg-[var(--glass-bg)] transition-all duration-[var(--dur)] ease-[var(--ease)]',
            'cursor-pointer',
          )}
          aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
        >
          <ThemeIcon size={16} />
        </button>

        {/* User menu */}
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={(): void => {
              setMenuOpen((v) => !v);
            }}
            className="cursor-pointer hover:opacity-90 transition-opacity"
          >
            <GlassAvatar name={getUserName(user)} size="sm" />
          </button>

          {menuOpen && (
            <div
              className="absolute right-0 mt-2 w-56 rounded-xl py-1 z-50"
              style={{
                background: 'var(--glass-bg-strong, rgba(255,255,255,0.80))',
                backdropFilter: 'blur(var(--glass-blur-heavy, 16px))',
                WebkitBackdropFilter: 'blur(var(--glass-blur-heavy, 16px))',
                border: '1px solid var(--glass-border, rgba(255,255,255,0.55))',
                boxShadow: 'var(--glass-shadow-lg, 0 8px 40px rgba(0,0,0,0.08))',
              }}
            >
              {user && (
                <div
                  className="px-4 py-2.5"
                  style={{
                    borderBottom: '1px solid var(--glass-border-subtle, rgba(255,255,255,0.30))',
                  }}
                >
                  <p className="text-sm font-medium text-text truncate">{user.email}</p>
                  <p className="text-xs text-text-secondary capitalize">{user.role}</p>
                </div>
              )}
              <button
                type="button"
                onClick={handleLogout}
                className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-danger transition-colors cursor-pointer"
                style={{ background: 'transparent' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background =
                    'var(--glass-bg-hover, rgba(255,255,255,0.45))';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <LogOut size={14} />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
